/**
 * Theme Provider
 *
 * Provides theme tokens to all components via React Context
 * Uses light theme to complement white product photography
 */

import { createContext, useContext, ReactNode } from 'react';
import { colors, typography, spacing, breakpoints, shadows, transitions } from '../tokens';

interface ThemeContextValue {
  colors: typeof colors;
  typography: typeof typography;
  spacing: typeof spacing;
  breakpoints: typeof breakpoints;
  shadows: typeof shadows;
  transitions: typeof transitions;
}

const ThemeContext = createContext<ThemeContextValue>({
  colors,
  typography,
  spacing,
  breakpoints,
  shadows,
  transitions,
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  return (
    <ThemeContext.Provider
      value={{
        colors,
        typography,
        spacing,
        breakpoints,
        shadows,
        transitions,
      }}
    >
      {/* Light theme - matches white product photography */}
      {children}
    </ThemeContext.Provider>
  );
};
